<script>
  // Checklist de ativação: mostra o que falta para a agenda começar a receber clientes.
  // Cada passo leva direto à tela certa. Usado inteiro (aba "Primeiros passos") e compacto (topo de Agendamentos).
  import { createEventDispatcher } from 'svelte';
  import { calcularPassos, proximoPasso, agendaPronta, gravarFlag, chaveViu, chaveDivulgou, mensagemCompartilhar } from '../../Classes/PassosAtivacao.js';

  export let tenant = null;
  export let locais = [];
  export let horarios = [];
  export let agendamentos = [];
  export let linkPublico = '';
  export let compacto = false;

  const dispatch = createEventDispatcher();

  // "versao" força recalcular depois de gravar as marcações no navegador (ver agenda / divulgar)
  let versao = 0;
  $: passos = (versao, calcularPassos({ tenant, locais, horarios, agendamentos }));
  $: essenciais = passos.filter(p => !p.opcional);
  $: feitos = passos.filter(p => p.feito).length;
  $: essenciaisFeitos = essenciais.every(p => p.feito);
  $: pronta = agendaPronta(passos);
  $: progresso = Math.round((feitos / passos.length) * 100);
  $: proximo = proximoPasso(passos);
  $: mostrarCreditos = tenant && (tenant.em_teste === true || tenant.em_teste == 1) && (pronta || Number(tenant.dias_restantes) <= 3);

  function executar(passo) {
    if (!passo) return;
    if (passo.tipo === 'ver') {
      gravarFlag(chaveViu(tenant)); versao++;
      window.open(linkPublico, '_blank');
      dispatch('passoConcluido', 'Você abriu a sua agenda.');
      return;
    }
    if (passo.tipo === 'divulgar') {
      gravarFlag(chaveDivulgou(tenant)); versao++;
      window.open(`https://wa.me/?text=${encodeURIComponent(mensagemCompartilhar(linkPublico))}`, '_blank');
      dispatch('passoConcluido', 'Link enviado!');
      return;
    }
    dispatch('mudarAba', passo.aba);
  }

  function copiarLink() {
    navigator.clipboard?.writeText(linkPublico);
    gravarFlag(chaveDivulgou(tenant)); versao++;
    dispatch('recarregar', 'Link copiado! Cole na bio do Instagram ou mande para seus clientes.');
  }
</script>

{#if compacto}
  <!-- Só aparece quando a agenda realmente não recebe clientes (falta local ou horários) -->
  {#if !pronta && proximo}
    <div class="checklist-compacto fade-in">
      <div class="cc-texto">
        <strong>Sua agenda ainda não está pronta para receber clientes</strong>
        <span>Próximo passo: {proximo.titulo.toLowerCase()}</span>
      </div>
      <button class="btn-primary" on:click={() => executar(proximo)}>{proximo.acao}</button>
    </div>
  {/if}
{:else}
  <div class="tutorial-container fade-in">
    <h2><i class="fas fa-rocket"></i> Primeiros passos</h2>
    {#if essenciaisFeitos}
      <p class="text-muted">Pronto! Sua agenda já pode receber clientes. Os passos abaixo marcados como opcionais deixam tudo ainda melhor.</p>
    {:else}
      <p class="text-muted">Faça os passos abaixo para seus clientes já conseguirem marcar. Leva uns 5 minutos.</p>
    {/if}

    <div class="progresso" aria-label="Progresso">
      <div class="progresso-barra" style="width: {progresso}%"></div>
    </div>
    <p class="progresso-texto">{feitos} de {passos.length} concluídos</p>

    <ol class="lista-passos">
      {#each passos as passo, i}
        <li class="passo {passo.feito ? 'feito' : ''} {proximo && proximo.id === passo.id ? 'proximo' : ''}">
          <div class="passo-status">
            {#if passo.feito}<i class="fas fa-check"></i>{:else}<span>{i + 1}</span>{/if}
          </div>
          <div class="passo-texto">
            <h3>{passo.titulo} {#if passo.opcional}<small>opcional</small>{/if}</h3>
            <p>{passo.desc}</p>
          </div>
          <button class="passo-acao {passo.feito ? 'secundario' : ''}" on:click={() => executar(passo)}>
            <i class="fas {passo.icone}"></i> {passo.feito ? 'Rever' : passo.acao}
          </button>
        </li>
      {/each}
    </ol>

    {#if mostrarCreditos}
      <div class="creditos-box">
        <div>
          <strong><i class="fas fa-coins"></i> Depois do teste</strong>
          <p>Para a sua agenda continuar no ar depois do teste grátis, compre créditos por PIX quando quiser. Cada compra soma dias de acesso: sem mensalidade, sem fidelidade e sem renovação automática.</p>
        </div>
        <button class="passo-acao secundario" on:click={() => dispatch('mudarAba', 'creditos')}>Ver créditos</button>
      </div>
    {/if}

    <div class="link-box">
      <div>
        <strong>Seu link de agendamento</strong>
        <a href={linkPublico} target="_blank">{linkPublico}</a>
      </div>
      <div class="link-botoes">
        <button class="passo-acao secundario" on:click={copiarLink}><i class="far fa-copy"></i> Copiar link</button>
        <button class="passo-acao whatsapp" on:click={() => executar(passos.find(p => p.id === 'divulgar'))}><i class="fab fa-whatsapp"></i> Enviar no WhatsApp</button>
      </div>
    </div>

    <div class="dicas">
      <h3><i class="fas fa-lightbulb"></i> Bom saber</h3>
      <ul>
        <li><strong>Cliente ligou ou mandou mensagem?</strong> Em <em>Horários disponíveis</em>, use o botão <em>Agendar</em> para marcar por ele e bloquear o horário.</li>
        <li><strong>Lembretes:</strong> o cliente recebe a confirmação por e-mail na hora e um lembrete na véspera. Você ajusta o texto em <em>Configurações</em>.</li>
        <li><strong>Cupom ou indicação:</strong> em <em>Meus créditos</em>, campo "Possui um cupom?", você resgata dias grátis ou desconto.</li>
      </ul>
    </div>
  </div>
{/if}

<style>
  .tutorial-container { max-width: 860px; }
  h2 { color: #4a6c56; margin: 0 0 6px 0; }
  .text-muted { color: #5a7063; margin: 0 0 18px 0; line-height: 1.5; }
  .progresso { height: 10px; background: #e2e8e4; border-radius: 99px; overflow: hidden; }
  .progresso-barra { height: 100%; background: #4a6c56; border-radius: 99px; transition: width 0.4s; }
  .progresso-texto { font-size: 0.8rem; color: #5a7063; margin: 6px 0 18px 0; font-weight: 600; }

  .lista-passos { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
  .passo { display: flex; align-items: center; gap: 16px; background: white; border: 1px solid #e2e8e4; border-radius: 12px; padding: 16px 18px; }
  .passo.proximo { border-color: #4a6c56; box-shadow: 0 6px 18px rgba(74,108,86,0.12); }
  .passo.feito { background: #f6faf7; }
  .passo.feito h3 { color: #5a7063; }
  .passo-status { flex: 0 0 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; background: #eef5f0; color: #4a6c56; }
  .passo.feito .passo-status { background: #4a6c56; color: white; }
  .passo-texto { flex: 1; min-width: 0; }
  .passo-texto h3 { margin: 0 0 4px 0; font-size: 1rem; color: #2d3748; }
  .passo-texto h3 small { font-size: 0.7rem; font-weight: 600; color: #8a9a8f; background: #f1f4f2; padding: 2px 8px; border-radius: 99px; margin-left: 6px; vertical-align: middle; }
  .passo-texto p { margin: 0; font-size: 0.88rem; color: #5a7063; line-height: 1.45; }
  .passo-acao { flex: 0 0 auto; background: #4a6c56; color: white; border: none; padding: 10px 16px; border-radius: 8px; font-weight: 700; font-size: 0.85rem; cursor: pointer; white-space: nowrap; font-family: inherit; }
  .passo-acao.secundario { background: white; color: #4a6c56; border: 1px solid #c9d6cd; }
  .passo-acao.whatsapp { background: #25d366; }

  .link-box { margin-top: 22px; background: #eef5f0; border-radius: 12px; padding: 16px 18px; display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; }
  .link-box strong { display: block; font-size: 0.8rem; color: #5a7063; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
  .link-box a { color: #4a6c56; font-weight: 800; word-break: break-all; }
  .link-botoes { display: flex; gap: 8px; flex-wrap: wrap; }

  .creditos-box { margin-top: 22px; border: 1px dashed #cfaa6c; background: #fffaf0; border-radius: 12px; padding: 16px 18px; display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; }
  .creditos-box strong { color: #8a6a2f; }
  .creditos-box p { margin: 4px 0 0 0; font-size: 0.88rem; color: #6b5a3a; line-height: 1.5; max-width: 560px; }
  .dicas { margin-top: 22px; background: white; border: 1px solid #e2e8e4; border-radius: 12px; padding: 16px 20px; }
  .dicas h3 { margin: 0 0 8px 0; font-size: 0.95rem; color: #4a6c56; }
  .dicas ul { margin: 0; padding-left: 18px; color: #4a5a50; font-size: 0.88rem; line-height: 1.6; }

  .checklist-compacto { display: flex; align-items: center; justify-content: space-between; gap: 14px; background: #fff9e6; border: 1px solid #f6d58a; border-radius: 12px; padding: 14px 18px; margin-bottom: 20px; }
  .cc-texto strong { display: block; color: #744210; font-size: 0.95rem; }
  .cc-texto span { font-size: 0.85rem; color: #8a5a1a; }
  .checklist-compacto .btn-primary { padding: 10px 18px; white-space: nowrap; }

  @media (max-width: 640px) {
    .passo { flex-wrap: wrap; padding: 14px; }
    .passo-acao { width: 100%; text-align: center; }
    .checklist-compacto { flex-direction: column; align-items: stretch; }
  }
</style>
